'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import LoadingOverlay from '@/components/LoadingOverlay';

// Shows the loading overlay from the moment an internal link is clicked until
// the new route has rendered, so a click never looks like it did nothing.
//
// This replaces a root app/loading.tsx. That file wrapped every page in a
// Suspense boundary, which made Next stream a 200 response before a page
// could call notFound()/redirect() - missing products were served as
// "soft 404s" (HTTP 200 + noindex) and redirects as client-side meta refreshes.
export default function NavigationFeedback() {
  const pathname = usePathname();
  // Path the user was on when they clicked a link; navigation is pending
  // until the router lands on a different path.
  const [clickedFrom, setClickedFrom] = useState<string | null>(null);
  // Reset during render (not in an effect) once the route has changed.
  if (clickedFrom !== null && clickedFrom !== pathname) setClickedFrom(null);
  const pending = clickedFrom !== null;

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      // No defaultPrevented check: next/link always prevents the default to do
      // a client-side navigation. In-page "#" links (lightbox, table of
      // contents) keep the same pathname and are skipped below.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;

      setClickedFrom(pathname);
    }

    // Capture phase: runs before next/link's own click handler
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [pathname]);

  // Never leave the overlay stuck if a navigation is cancelled or fails.
  useEffect(() => {
    if (!pending) return;
    const timeout = window.setTimeout(() => setClickedFrom(null), 10000);
    return () => window.clearTimeout(timeout);
  }, [pending]);

  return <LoadingOverlay loading={pending} />;
}
